import { z } from "zod";
import { AppliedScenarioMetricKeySchema } from "./applied-scenario-metrics.js";

// Spec 077 — a CLASSIFICAÇÃO da divergência (#1317, iteração 3).
//
// A apuração da 075/076 responde "quanto divergiu". Este contrato responde "POR QUÊ" — e a resposta
// é sempre um julgamento humano sobre uma proposta determinística, nunca o contrário.
//
// Arquivo separado de `applied-scenarios.ts` por duas razões que se somam: o teto de 200 LOC
// efetivas da Constituição, e o fato de a fila de classificação ser cross-linha (não é leitura de
// UM compromisso). A regra que decide qualquer coisa aqui mora em `@opus-dt/closed-loop` —
// `proposeCause`, `allowedCausesFor`, `pendencyKindFor`, `requiresTarget`. Este arquivo só declara
// a FORMA que atravessa o HTTP.

/**
 * O vocabulário FECHADO das cinco causas.
 *
 * ⚠️ As duas primeiras NÃO são de livre escolha (FR-011): `WITHIN_TOLERANCE` exige toda métrica
 * apurada dentro da faixa declarada, e `EXECUTION_NON_ADHERENCE` exige o índice congelado abaixo do
 * limiar (ou nulo). São exatamente as duas que decidem se o compromisso entra na estatística de
 * acurácia, e ambas já foram decididas por fatos congelados ANTES de alguém ver o resultado.
 * Deixá-las livres abriria a porta dos fundos do viés de seleção que a #1317 identificou como a
 * armadilha principal. A trava é do SERVIDOR (`allowedCausesFor`), nunca da tela.
 */
export const DivergenceCauseSchema = z.enum([
  "WITHIN_TOLERANCE",
  "EXECUTION_NON_ADHERENCE",
  "UNMODELED_EVENT",
  "INCORRECT_ASSUMPTION",
  "STRUCTURAL_MODEL_ERROR",
]);
export type DivergenceCause = z.infer<typeof DivergenceCauseSchema>;

/** DERIVADO da causa, nunca escolhido (FR-019). As duas causas não acionáveis não mapeiam para nada. */
export const CalibrationPendencyKindSchema = z.enum(["MODEL_EVENT", "MEASURE_PARAM", "ENGINE_FIX"]);
export type CalibrationPendencyKind = z.infer<typeof CalibrationPendencyKindSchema>;

/** Os três desfechos são TERMINAIS. `SUPERSEDED` é efeito de reclassificação, nunca ação de usuário. */
export const CalibrationPendencyStatusSchema = z.enum([
  "OPEN",
  "RESOLVED",
  "DISCARDED",
  "SUPERSEDED",
]);
export type CalibrationPendencyStatus = z.infer<typeof CalibrationPendencyStatusSchema>;

/** A métrica como a evidência de tolerância a mostra — sem os deltas, que a apuração já traz. */
export const EvidenceMetricSchema = z.object({
  metric: AppliedScenarioMetricKeySchema,
  predicted: z.number().finite().nullable(),
  realized: z.number().finite().nullable(),
  tolerancePct: z.number().finite().nullable(),
  withinTolerance: z.boolean().nullable(),
});

/**
 * A EVIDÊNCIA, discriminada pelo ramo da cascata que decidiu — não pela causa.
 *
 * `none` cobre DOIS casos que a tela precisa distinguir e que só `needsDetail` separa: a regra não
 * soube (ramo 4) e a interferência não foi consultada (ramo 3b, o caso da fila). Um `reason`
 * literal só, porque a distinção não é da evidência: é de quanto se perguntou.
 */
export const ProposalEvidenceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("adherence"),
    index: z.number().finite().nullable(),
    threshold: z.number().finite(),
    driverDimension: z.string().nullable(),
  }),
  z.object({ kind: z.literal("tolerance"), metrics: z.array(EvidenceMetricSchema) }),
  z.object({
    kind: z.literal("interference"),
    orders: z.array(
      z.object({
        productionOrderId: z.string().uuid(),
        code: z.string().nullable(),
        producedQty: z.number().finite(),
      })
    ),
  }),
  z.object({ kind: z.literal("none"), reason: z.literal("no_rule_applies") }),
]);
export type ProposalEvidence = z.infer<typeof ProposalEvidenceSchema>;

export const ClassificationTargetSchema = z.object({
  machineId: z.string().uuid(),
  machineName: z.string(),
  parameterId: z.string().uuid(),
  parameterName: z.string(),
});

/** A pendência como o DETALHE do compromisso a mostra — a fila tem projeção própria e mais rica. */
export const ClassificationPendencySchema = z.object({
  id: z.string().uuid(),
  kind: CalibrationPendencyKindSchema,
  status: CalibrationPendencyStatusSchema,
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolutionNote: z.string().nullable(),
});

export const ClassificationEntrySchema = z.object({
  id: z.string().uuid(),
  cause: DivergenceCauseSchema,
  confirmedByName: z.string().nullable(),
  confirmedAt: z.string(),
  note: z.string().nullable(),
  /** O que o sistema havia proposto NAQUELA ocasião. `null` = a regra não soube — nunca "nenhuma". */
  proposedCause: DivergenceCauseSchema.nullable(),
  proposalOrigin: z.string(),
  target: ClassificationTargetSchema.nullable(),
  /**
   * ⚠️ CADA entrada carrega a SUA pendência, inclusive as de classificações superadas (FR-027). O
   * trabalho de calibração foi feito; escondê-lo porque o diagnóstico mudou apagaria justamente o
   * registro que a #1317 pede para preservar.
   */
  pendency: ClassificationPendencySchema.nullable(),
});

export const CauseProposalSchema = z.object({
  cause: DivergenceCauseSchema.nullable(),
  /** `rules` hoje — a costura para um proponente assistido que use a mesma forma. */
  origin: z.string(),
  evidence: ProposalEvidenceSchema,
  needsDetail: z.boolean(),
});

/**
 * O campo `classification` do detalhe do compromisso. `null` enquanto ele não está `SETTLED`
 * (FR-048): antes da apuração não há o que classificar, e a tela MUST dizer isso — nunca oferecer
 * formulário desabilitado e mudo.
 */
export const ClassificationViewSchema = z.object({
  current: ClassificationEntrySchema.nullable(),
  /** Derivada na leitura, e SÓ enquanto ninguém classificou (R1). */
  proposal: CauseProposalSchema.nullable(),
  /**
   * ⚠️ Vem do SERVIDOR e não é recalculada na tela. A trava de FR-011 é regra de domínio; uma
   * segunda implementação no cliente é exatamente a divergência que a trava existe para impedir.
   */
  allowedCauses: z.array(DivergenceCauseSchema),
  /** Cada causa vedada viaja com o motivo PRONTO, para a tela exibi-la desabilitada e explicada. */
  blockedCauses: z.array(z.object({ cause: DivergenceCauseSchema, reason: z.string() })),
  history: z.array(ClassificationEntrySchema),
  /** A pendência da classificação VIGENTE — atalho do que já está em `history`. */
  pendency: ClassificationPendencySchema.nullable(),
  /**
   * O ESCOPO em que um alvo pode ser apontado (FR-020) — as máquinas congeladas no ato de aplicar.
   *
   * Viaja DENTRO de `classification`, e não como campo irmão do compromisso, porque é propriedade
   * da superfície de classificação e não da forma geral do compromisso. Sem ele, a tela ofereceria
   * as máquinas atuais da linha — que podem já não ser as comprometidas —, e cada tentativa de
   * apontar uma delas viraria um `422` do servidor, que revalida de qualquer forma (V5).
   */
  committedMachineIds: z.array(z.string().uuid()),
});
export type ClassificationView = z.infer<typeof ClassificationViewSchema>;

/**
 * O corpo de `POST /applied-scenarios/:id/classification`.
 *
 * O `.superRefine` cobre V4 e V6 — alvo obrigatório em `INCORRECT_ASSUMPTION` e proibido nas demais
 * — porque as duas são decidíveis pelo próprio corpo, e um `400` antes de tocar o banco é mais
 * honesto que um `422` depois. V3 (nota obrigatória) depende da proposta derivada, que só o service
 * conhece: fica no service, como `422`.
 */
export const ConfirmClassificationBodySchema = z
  .object({
    cause: DivergenceCauseSchema,
    note: z.string().min(1).max(1000).optional(),
    target: z.object({ machineId: z.string().uuid(), parameterId: z.string().uuid() }).optional(),
  })
  .superRefine((body, ctx) => {
    // A regra de quem exige alvo mora em `requiresTarget` (`@opus-dt/closed-loop`); repeti-la aqui
    // seria a segunda implementação. O contrato não pode importar o pacote de domínio, então o que
    // se repete é o VALOR, não a regra — e o teste da cascata é o que impede os dois de divergirem.
    const exige = body.cause === "INCORRECT_ASSUMPTION";
    if (exige && !body.target) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["target"],
        message: "Premissa incorreta exige apontar a máquina e o parâmetro.",
      });
    }
    if (!exige && body.target) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["target"],
        message: "Só a causa de premissa incorreta aceita um alvo de calibração.",
      });
    }
  });
export type ConfirmClassificationBody = z.infer<typeof ConfirmClassificationBodySchema>;

// ── A fila 1: apurados que ninguém julgou ──────────────────────────────────────────────────────

export const AwaitingClassificationQuerySchema = z.object({
  lineId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});

export const AwaitingClassificationItemSchema = z.object({
  appliedScenarioId: z.string().uuid(),
  lineId: z.string().uuid(),
  lineName: z.string(),
  windowStart: z.string(),
  windowEnd: z.string(),
  settledAt: z.string(),
  /** A de MAIOR desvio relativo contra o previsto (FR-016). `null` = nenhuma era comparável. */
  topDeviationMetric: z
    .object({
      metric: AppliedScenarioMetricKeySchema,
      predicted: z.number().finite().nullable(),
      realized: z.number().finite().nullable(),
      deltaPct: z.number().finite().nullable(),
    })
    .nullable(),
  proposal: CauseProposalSchema.omit({ evidence: true }),
  allowedCauses: z.array(DivergenceCauseSchema),
});

export const AwaitingClassificationListEnvelopeSchema = z.object({
  data: z.object({
    /** Contagem REAL, independente da página (FR-037) — nunca `items.length`. */
    total: z.number().int(),
    items: z.array(AwaitingClassificationItemSchema),
  }),
  error: z.null(),
});
export type AwaitingClassificationItem = z.infer<typeof AwaitingClassificationItemSchema>;
